/**
 * AI Assistant — host-side configuration hook.
 *
 * Reads `JSDB().apiConfig.ai` and resolves the right token + base URL for
 * the current logged-in agent. Mirrors the WhatsApp pattern (`useWhatsAppConfig`)
 * so onboarding feels familiar — admin pastes a JSON blob into the company's
 * `api_config.ai` field and any user can plug-and-play the assistant.
 *
 * Naming model:
 *
 *   • nihulkav            = the SaaS product (one tenant in `ai_companies`)
 *   • {company}           = a customer company on NihulKav (e.g. `test`,
 *                           `avikima`, `galrazit`). Each company has its OWN
 *                           api_config row, so this whole JSON is implicitly
 *                           scoped to one company already — the company name
 *                           just has to be DECLARED here so we can compose
 *                           the globally-unique `ai_users.user_id`.
 *   • {user}              = an agent (logged-in employee) inside that
 *                           company. `JSDB().currentAgent` returns this.
 *   • ai_users.user_id    = `{company}_{user}` (e.g. `test_yotam`). Globally
 *                           unique across every company on every software,
 *                           so the AI admin / cost / chat history doesn't
 *                           collide between two `yotam`s in different orgs.
 *
 * Expected shape of `api_config.ai` (tokens + infra only — no feature flags):
 *
 *   {
 *     "default": {
 *       "company": "alkimaycompany",
 *       "baseUrl": "https://ai.solvitcompany.com",
 *       "apiBase": "https://ai.solvitcompany.com",
 *       "token":   "nk_live_shared_fallback_token"
 *     },
 *     "users": {
 *       "yotam": "nk_live_yotam_personal_token",
 *       "moshe": { "token": "nk_live_moshe", "userId": "alkimaycompany_moshe" }
 *     }
 *   }
 *
 * What is NOT in this JSON: scopes, agents, feature toggles. Those come only
 * from the bearer token (`GET /me/info` → scopes; see useAiAssistentInfo).
 */
import { useMemo } from 'react';
import JSDB from '../fromKotlin/nk';

export interface AiHostConfig {
    /** Where the iframe widget is hosted (loaded by AiAssistantDrawer). */
    baseUrl: string;
    /** Backend FastAPI base — passed to the iframe so it knows where to call. */
    apiBase: string;
    /** Per-user bearer token (issued in the AI admin UI). */
    token: string;
    /** Logical user id for embed (`{company}_{agent}`); identity only, not RBAC. */
    userId: string;
    /** Chat embed startup agent — fixed in code, not read from api_config.ai. */
    agentId: string;
}

const DEFAULT_BASE_URL = 'https://ai.solvitcompany.com';
const DEFAULT_API_BASE = 'https://ai.solvitcompany.com';
const DEFAULT_AGENT_ID = 'supervisor';

// ─────────────────────────────────────────────────────────────────────────────
// 🚧 TEMP DEV OVERRIDE — paste a test token below, save, refresh nexilcolab.
// When `token` is non-empty, the normal `api_config.ai` lookup is skipped and
// EVERY user gets this config. Use only for local smoke-testing the embed flow.
// ⚠️  NEVER COMMIT A REAL TOKEN HERE — a token in source is a credential leak
//     even when the override branch is disabled.
// ─────────────────────────────────────────────────────────────────────────────
const HARDCODED_TEST_CONFIG: Partial<AiHostConfig> = {
    token: '',                            // 👈 dev-only; keep blank in git
    userId: '',                           // e.g. 'nihulkav_yotam'
    // Local dev (vite 5173 + uvicorn 8100):
    baseUrl: 'http://localhost:5173',
    apiBase: 'http://localhost:8100',
    // Production — both are https://ai.solvitcompany.com (Apache :443 proxies to uvicorn :8100).
    // Do NOT use :8100 in the browser; that port is not open on the public internet.
};

/** Best-effort agent (= username) discovery for the current session. */
function _resolveAgentName(): string {
    try {
        const explicit = (JSDB() as any).getUserName?.();
        if (explicit) return String(explicit);
    } catch { /* */ }
    try {
        return String(JSDB().currentAgent || '').trim();
    } catch {
        return '';
    }
}

/**
 * Compose the globally-unique `ai_users.user_id` from the company name
 * (declared in `default.company`) and the current agent name. Falls back
 * to whatever bits we have so the widget never hard-fails.
 */
function _composeUserId(companyHint: string, agent: string): string {
    const company = (companyHint || '').trim();
    const a = (agent || '').trim();
    if (company && a) return `${company}_${a}`;
    if (a) return a;
    return 'unknown';
}

export function getAiConfig(): AiHostConfig | null {
    // TEMP dev override: short-circuit when the hardcoded token is filled in.
    if (HARDCODED_TEST_CONFIG.token) {
        const agent = _resolveAgentName();
        return {
            token:   HARDCODED_TEST_CONFIG.token,
            userId:  HARDCODED_TEST_CONFIG.userId  || _composeUserId('', agent),
            baseUrl: (HARDCODED_TEST_CONFIG.baseUrl || DEFAULT_BASE_URL).replace(/\/+$/, ''),
            apiBase: (HARDCODED_TEST_CONFIG.apiBase || DEFAULT_API_BASE).replace(/\/+$/, ''),
            agentId: DEFAULT_AGENT_ID,
        };
    }

    let raw: any;
    try {
        
        raw = (JSDB().apiConfig as { ai?: string | object })?.ai;
    } catch {
        return null;
    }
    if (!raw) return null;
    let parsed: any = raw;
    if (typeof raw === 'string') {
        try { parsed = JSON.parse(raw); } catch { return null; }
    }
    if (typeof parsed !== 'object' || parsed === null) return null;

    const def: any = parsed.default || {};
    const users: any = parsed.users || {};

    const agentName = _resolveAgentName();
    // Company name MUST come from the JSON — this api_config row is already
    // scoped to one NihulKav company so the admin declares it once.
    const companyName = String(def.company || '').trim();
    const composedUserId = _composeUserId(companyName, agentName);

    // Look up the per-user entry. Try the exact agent key first (the
    // documented form), then the fully-composed id (legacy / explicit).
    const candidates = [users[agentName], users[composedUserId]].filter(Boolean);
    let userEntry: any = candidates[0];
    if (typeof userEntry === 'string') {
        userEntry = { token: userEntry };
    }

    const token = String(userEntry?.token || def?.token || '').trim();
    if (!token) return null;

    // Per-user `userId` override beats the composed value (escape hatch for
    // legacy rows where a single user's id doesn't follow the convention).
    const userId = String(userEntry?.userId || composedUserId);

    const baseUrl = String(userEntry?.baseUrl || def?.baseUrl || DEFAULT_BASE_URL).replace(/\/+$/, '');
    const apiBase = String(userEntry?.apiBase || def?.apiBase || DEFAULT_API_BASE).replace(/\/+$/, '');

    return { baseUrl, apiBase, token, userId, agentId: DEFAULT_AGENT_ID };
}

export function useAiConfig(): AiHostConfig | null {
    return useMemo(() => getAiConfig(), []);
}

/** True while `HARDCODED_TEST_CONFIG.token` is set (dev smoke-test). */
export function isUsingHardcodedAiTestConfig(): boolean {
    return Boolean(String(HARDCODED_TEST_CONFIG.token || '').trim());
}
